// src/pages/Settings/EpfPercentage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Badge,
  Alert,
  Spinner,
  Input,
  Label,
  FormGroup,
  FormFeedback,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
} from 'reactstrap';
import {
  Plus,
  Edit2,
  RefreshCw,
  AlertCircle,
  Save,
  X,
  Calendar,
  Percent,
} from 'react-feather';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { toast } from 'react-toastify';
import { apiClient } from '../../helpers/api_helper';

// ─────────────────────── types ───────────────────────
interface EpfRate {
  id: number;
  effective_from: string;          // ISO date string
  epf_employee: string | number;   // Prisma Decimal comes back as string
  epf_employer: string | number;
  esi_employee: string | number;
  esi_employer: string | number;
  epf_wage_ceiling: string | number;
  esi_gross_ceiling: string | number;
  created_at: string;
}

interface FormState {
  effective_from: string;   // "YYYY-MM"
  epf_employee: string;
  epf_employer: string;
  esi_employee: string;
  esi_employer: string;
  epf_wage_ceiling: string;
  esi_gross_ceiling: string;
}

interface FormErrors {
  effective_from?: string;
  epf_employee?: string;
  epf_employer?: string;
  esi_employee?: string;
  esi_employer?: string;
  epf_wage_ceiling?: string;
  esi_gross_ceiling?: string;
}

// ─────────────────────── helpers ───────────────────────
const toMonthInput = (iso: string): string => {
  // incoming: "2026-04-01T00:00:00.000Z" → "2026-04"
  const d = new Date(iso);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};

const toMonthLabel = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const emptyForm = (): FormState => ({
  effective_from: '',
  epf_employee: '12',
  epf_employer: '12',
  esi_employee: '0.75',
  esi_employer: '3.25',
  epf_wage_ceiling: '15000',
  esi_gross_ceiling: '21000',
});

const formFromRow = (row: EpfRate): FormState => ({
  effective_from: toMonthInput(row.effective_from),
  epf_employee: String(row.epf_employee),
  epf_employer: String(row.epf_employer),
  esi_employee: String(row.esi_employee),
  esi_employer: String(row.esi_employer),
  epf_wage_ceiling: String(row.epf_wage_ceiling),
  esi_gross_ceiling: String(row.esi_gross_ceiling),
});

const isPositiveNumber = (v: string): boolean => {
  if (v.trim() === '') return false;
  const n = Number(v);
  return !Number.isNaN(n) && n >= 0;
};

// ─────────────────────── page ───────────────────────
const EpfPercentage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<EpfRate[]>([]);
  const [error, setError] = useState('');

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const isEdit = editingId !== null;

  // ── fetch list ──
  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/epf-percentage');
      if (res?.success) {
        setRows(res.data || []);
      } else {
        setError(res?.message || 'Failed to fetch rates');
        toast.error(res?.message || 'Failed to fetch rates');
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to fetch rates');
      toast.error(err.message || 'Failed to fetch rates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // ── form change ──
  const setField = (k: keyof FormState, v: string) => {
    setForm((p) => ({ ...p, [k]: v }));
    setFormErrors((p) => ({ ...p, [k]: undefined }));
  };

  // ── validate ──
  const validate = (): boolean => {
    const errs: FormErrors = {};
    if (!form.effective_from) {
      errs.effective_from = 'Effective month is required';
    }
    if (!isPositiveNumber(form.epf_employee)) {
      errs.epf_employee = 'Required, must be ≥ 0';
    }
    if (!isPositiveNumber(form.epf_employer)) {
      errs.epf_employer = 'Required, must be ≥ 0';
    }
    if (!isPositiveNumber(form.esi_employee)) {
      errs.esi_employee = 'Required, must be ≥ 0';
    }
    if (!isPositiveNumber(form.esi_employer)) {
      errs.esi_employer = 'Required, must be ≥ 0';
    }
    if (!isPositiveNumber(form.epf_wage_ceiling)) {
      errs.epf_wage_ceiling = 'Required, must be ≥ 0';
    }
    if (!isPositiveNumber(form.esi_gross_ceiling)) {
      errs.esi_gross_ceiling = 'Required, must be ≥ 0';
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── open create ──
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormErrors({});
    setModalOpen(true);
  };

  // ── open edit ──
  const openEdit = (row: EpfRate) => {
    setEditingId(row.id);
    setForm(formFromRow(row));
    setFormErrors({});
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  // ── submit ──
  const handleSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      // "YYYY-MM" → ISO "YYYY-MM-01T00:00:00.000Z"
      const effective_from = `${form.effective_from}-01T00:00:00.000Z`;

      const payload = {
        effective_from,
        epf_employee: Number(form.epf_employee),
        epf_employer: Number(form.epf_employer),
        esi_employee: Number(form.esi_employee),
        esi_employer: Number(form.esi_employer),
        epf_wage_ceiling: Number(form.epf_wage_ceiling),
        esi_gross_ceiling: Number(form.esi_gross_ceiling),
      };

      if (isEdit) {
        await apiClient.put(`/epf-percentage/${editingId}`, payload);
        toast.success('Rate updated successfully');
      } else {
        await apiClient.post('/epf-percentage', payload);
        toast.success('Rate added successfully');
      }

      setModalOpen(false);
      fetchRows();
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── render ──
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="EPF / ESI Rates" pageTitle="Settings" />

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h5 className="card-title mb-0">
                    <Percent size={18} className="me-2" />
                    Statutory Rates
                  </h5>
                  <small className="text-muted">
                    Rates apply from the selected effective month. Old months keep using the previous rates.
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <Button color="primary" size="sm" onClick={openCreate}>
                    <Plus size={14} className="me-1" />
                    Add Rate
                  </Button>
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={fetchRows}
                    disabled={loading}
                  >
                    <RefreshCw size={14} className="me-1" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>

              <CardBody>
                {error && (
                  <Alert color="danger" className="mb-3">
                    <AlertCircle size={16} className="me-2" />
                    {error}
                  </Alert>
                )}

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" className="me-2" />
                    <span>Loading rates…</span>
                  </div>
                ) : rows.length === 0 ? (
                  <div className="text-center py-5">
                    <Calendar size={48} className="text-muted mb-3" />
                    <h5>No rates configured</h5>
                    <p className="text-muted mb-3">
                      Add the first statutory rate to get started.
                    </p>
                    <Button color="primary" size="sm" onClick={openCreate}>
                      <Plus size={14} className="me-1" />
                      Add Rate
                    </Button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table bordered hover striped size="sm" className="mb-0 align-middle">
                      <thead className="table-light text-center">
                        <tr>
                          <th>S.No</th>
                          <th>Effective From</th>
                          <th>EPF Employee %</th>
                          <th>EPF Employer %</th>
                          <th>ESI Employee %</th>
                          <th>ESI Employer %</th>
                          <th>EPF Ceiling (₹)</th>
                          <th>ESI Ceiling (₹)</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, index) => (
                          <tr key={row.id}>
                            <td className="text-center">{index + 1}</td>
                            <td>
                              <div className="fw-semibold">
                                {toMonthLabel(row.effective_from)}
                              </div>
                              <small className="text-muted">
                                {toMonthInput(row.effective_from)}
                              </small>
                            </td>
                            <td className="text-center">
                              <Badge color="info" pill>
                                {Number(row.epf_employee).toFixed(2)}%
                              </Badge>
                            </td>
                            <td className="text-center">
                              <Badge color="primary" pill>
                                {Number(row.epf_employer).toFixed(2)}%
                              </Badge>
                            </td>
                            <td className="text-center">
                              <Badge color="warning" pill>
                                {Number(row.esi_employee).toFixed(2)}%
                              </Badge>
                            </td>
                            <td className="text-center">
                              <Badge color="success" pill>
                                {Number(row.esi_employer).toFixed(2)}%
                              </Badge>
                            </td>
                            <td className="text-end">
                              ₹
                              {Number(row.epf_wage_ceiling).toLocaleString('en-IN', {
                                minimumFractionDigits: 2,
                              })}
                            </td>
                            <td className="text-end">
                              ₹
                              {Number(row.esi_gross_ceiling).toLocaleString('en-IN', {
                                minimumFractionDigits: 2,
                              })}
                            </td>
                            <td className="text-center">
                              <Button
                                color="link"
                                size="sm"
                                className="p-0"
                                onClick={() => openEdit(row)}
                                title="Edit"
                              >
                                <Edit2 size={16} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ─────────────────────── Create / Edit Modal ─────────────────────── */}
      <Modal isOpen={modalOpen} toggle={closeModal} size="lg" backdrop={saving ? 'static' : true}>
        <ModalHeader toggle={closeModal}>
          {isEdit ? (
            <>
              <Edit2 size={18} className="me-2" />
              Edit Statutory Rate
            </>
          ) : (
            <>
              <Plus size={18} className="me-2" />
              Add Statutory Rate
            </>
          )}
        </ModalHeader>

        <ModalBody>
          {isEdit && (
            <Alert color="warning" className="mb-3">
              <AlertCircle size={16} className="me-2" />
              You are editing an existing rate. Saving will change it for
              every month it applies to, including past months.
            </Alert>
          )}

          <Row className="g-3">
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold text-muted small text-uppercase">
                  Effective From <span className="text-danger">*</span>
                </Label>
                <Input
                  type="month"
                  value={form.effective_from}
                  invalid={!!formErrors.effective_from}
                  onChange={(e) => setField('effective_from', e.target.value)}
                />
                <FormFeedback>{formErrors.effective_from}</FormFeedback>
                <small className="text-muted">
                  Only the month matters; the rate applies from the 1st.
                </small>
              </FormGroup>
            </Col>
          </Row>

          <hr className="my-3" />
          <h6 className="text-muted small text-uppercase mb-3">EPF (Provident Fund)</h6>

          <Row className="g-3">
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold text-muted small text-uppercase">
                  EPF Employee % <span className="text-danger">*</span>
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.epf_employee}
                  invalid={!!formErrors.epf_employee}
                  onChange={(e) => setField('epf_employee', e.target.value)}
                />
                <FormFeedback>{formErrors.epf_employee}</FormFeedback>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold text-muted small text-uppercase">
                  EPF Employer % <span className="text-danger">*</span>
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.epf_employer}
                  invalid={!!formErrors.epf_employer}
                  onChange={(e) => setField('epf_employer', e.target.value)}
                />
                <FormFeedback>{formErrors.epf_employer}</FormFeedback>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold text-muted small text-uppercase">
                  EPF Wage Ceiling (₹)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.epf_wage_ceiling}
                  invalid={!!formErrors.epf_wage_ceiling}
                  onChange={(e) => setField('epf_wage_ceiling', e.target.value)}
                />
                <FormFeedback>{formErrors.epf_wage_ceiling}</FormFeedback>
                <small className="text-muted">
                  Basic salary above this is not counted for EPF.
                </small>
              </FormGroup>
            </Col>
          </Row>

          <hr className="my-3" />
          <h6 className="text-muted small text-uppercase mb-3">ESI (Employee State Insurance)</h6>

          <Row className="g-3">
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold text-muted small text-uppercase">
                  ESI Employee % <span className="text-danger">*</span>
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.esi_employee}
                  invalid={!!formErrors.esi_employee}
                  onChange={(e) => setField('esi_employee', e.target.value)}
                />
                <FormFeedback>{formErrors.esi_employee}</FormFeedback>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold text-muted small text-uppercase">
                  ESI Employer % <span className="text-danger">*</span>
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.esi_employer}
                  invalid={!!formErrors.esi_employer}
                  onChange={(e) => setField('esi_employer', e.target.value)}
                />
                <FormFeedback>{formErrors.esi_employer}</FormFeedback>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold text-muted small text-uppercase">
                  ESI Gross Ceiling (₹)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.esi_gross_ceiling}
                  invalid={!!formErrors.esi_gross_ceiling}
                  onChange={(e) => setField('esi_gross_ceiling', e.target.value)}
                />
                <FormFeedback>{formErrors.esi_gross_ceiling}</FormFeedback>
                <small className="text-muted">
                  Gross salary above this is not covered by ESI.
                </small>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter>
          <Button color="light" onClick={closeModal} disabled={saving}>
            <X size={14} className="me-1" />
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" className="me-1" />
                Saving…
              </>
            ) : (
              <>
                <Save size={14} className="me-1" />
                {isEdit ? 'Update Rate' : 'Add Rate'}
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default EpfPercentage;